import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, filter, distinctUntilChanged } from 'rxjs';
import { AuthService } from './auth.service';
import { TrpcService } from './trpc.service';

export type RecurringInvoiceCronReason = 'ok' | 'stale' | 'never-run';

export interface RecurringInvoiceCronStatus {
    lastTick: string | null;
    hoursSince: number | null;
    thresholdHours: number;
    stale: boolean;
    reason: RecurringInvoiceCronReason;
}

/**
 * Fetches and caches the recurring-invoice cron-freshness payload for admins.
 * AppLayout's banner and the admin dashboard's status card both subscribe to
 * `status$` so a single round-trip per admin session drives both surfaces.
 *
 * Refresh policy: fetched once when an admin logs in (or on app startup if
 * already authenticated as admin). No periodic poll — an admin can refresh
 * the page to re-check.
 */
@Injectable({ providedIn: 'root' })
export class RecurringInvoiceCronAlertService {
    private authService = inject(AuthService);
    private trpcService = inject(TrpcService);

    private statusSubject = new BehaviorSubject<RecurringInvoiceCronStatus | null>(null);
    public status$: Observable<RecurringInvoiceCronStatus | null> = this.statusSubject.asObservable();

    constructor() {
        this.authService.currentUser$
            .pipe(
                distinctUntilChanged((a, b) => a?.id === b?.id && a?.role === b?.role),
                filter((user) => user?.role === 'admin'),
            )
            .subscribe(() => this.refresh());

        this.authService.currentUser$
            .pipe(filter((user) => !user))
            .subscribe(() => this.statusSubject.next(null));
    }

    async refresh(): Promise<void> {
        try {
            const status = await this.trpcService.admin.getRecurringInvoiceCronStatus.query();
            this.statusSubject.next(status as RecurringInvoiceCronStatus);
        } catch (error) {
            console.error('[RecurringInvoiceCronAlertService] refresh failed:', error);
            this.statusSubject.next(null);
        }
    }

    getCurrent(): RecurringInvoiceCronStatus | null {
        return this.statusSubject.value;
    }
}
